import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, SparklesIcon, ChartBarIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import FeatureCard from '../components/FeatureCard';
import StatCard from '../components/StatCard';
import FeatureModal from '../components/FeatureModal';
import StatModal from '../components/StatModal';
import ConfirmDialog from '../components/ConfirmDialog';

const WhyChoosePage = () => {
    const [activeTab, setActiveTab] = useState('features');
    const [features, setFeatures] = useState([]);
    const [stats, setStats] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
    const [isStatModalOpen, setIsStatModalOpen] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);
    const [editingStat, setEditingStat] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteFeatureConfirm, setDeleteFeatureConfirm] = useState({ isOpen: false, featureId: null });
    const [deleteStatConfirm, setDeleteStatConfirm] = useState({ isOpen: false, statId: null });

    // Fetch features
    const fetchFeatures = async () => {
        try {
            const response = await axios.get('/api/admin/whychoose/features');
            setFeatures(response.data.data || []);
        } catch (error) {
            console.error('Error fetching features:', error);
            toast.error('Failed to load features');
        }
    };

    // Fetch stats
    const fetchStats = async () => {
        try {
            const response = await axios.get('/api/admin/whychoose/stats');
            setStats(response.data.data || []);
        } catch (error) {
            console.error('Error fetching stats:', error);
            toast.error('Failed to load stats');
        }
    };

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/admin/whychoose/settings');
            setSettings(response.data.data || {
                section_title: 'Why Choose Aham Housing Finance?',
                section_subtitle: 'Your trusted partner in home financing',
                cta_text: 'Get Started',
                cta_link: '/contact'
            });
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        }
    };

    // Fetch all data
    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([fetchFeatures(), fetchStats(), fetchSettings()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Feature handlers
    const handleAddFeature = () => {
        setEditingFeature(null);
        setIsFeatureModalOpen(true);
    };

    const handleEditFeature = (feature) => {
        setEditingFeature(feature);
        setIsFeatureModalOpen(true);
    };

    const handleDeleteFeature = (featureId) => {
        setDeleteFeatureConfirm({ isOpen: true, featureId });
    };

    const confirmDeleteFeature = async () => {
        try {
            await axios.delete(`/api/admin/whychoose/features/${deleteFeatureConfirm.featureId}`);
            toast.success('Feature deleted successfully');
            fetchFeatures();
        } catch (error) {
            console.error('Error deleting feature:', error);
            toast.error('Failed to delete feature');
        }
    };

    const handleFeatureModalClose = () => {
        setIsFeatureModalOpen(false);
        setEditingFeature(null);
    };

    const handleFeatureSave = () => {
        handleFeatureModalClose();
        fetchFeatures();
    };

    // Stat handlers
    const handleAddStat = () => {
        setEditingStat(null);
        setIsStatModalOpen(true);
    };

    const handleEditStat = (stat) => {
        setEditingStat(stat);
        setIsStatModalOpen(true);
    };

    const handleDeleteStat = (statId) => {
        setDeleteStatConfirm({ isOpen: true, statId });
    };

    const confirmDeleteStat = async () => {
        try {
            await axios.delete(`/api/admin/whychoose/stats/${deleteStatConfirm.statId}`);
            toast.success('Stat deleted successfully');
            fetchStats();
        } catch (error) {
            console.error('Error deleting stat:', error);
            toast.error('Failed to delete stat');
        }
    };

    const handleStatModalClose = () => {
        setIsStatModalOpen(false);
        setEditingStat(null);
    };

    const handleStatSave = () => {
        handleStatModalClose();
        fetchStats();
    };

    // Settings handlers
    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/whychoose/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Why Choose Us</h1>
                    <p className="text-slate-500 mt-1">Manage features and statistics section</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-1.5 inline-flex gap-1">
                <button
                    onClick={() => setActiveTab('features')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'features'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <SparklesIcon className="h-5 w-5" />
                    Features ({features.length})
                </button>
                <button
                    onClick={() => setActiveTab('stats')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'stats'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <ChartBarIcon className="h-5 w-5" />
                    Statistics ({stats.length})
                </button>
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'settings'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <Cog6ToothIcon className="h-5 w-5" />
                    Settings
                </button>
            </div>

            {/* Features Tab */}
            {activeTab === 'features' && (
                <div className="space-y-6">
                    {/* Add Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleAddFeature}
                            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Add New Feature
                        </button>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                                    <div className="w-16 h-16 bg-slate-200 rounded-2xl mb-4"></div>
                                    <div className="h-6 bg-slate-200 rounded mb-2"></div>
                                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    ) : features.length === 0 ? (
                        /* Empty State */
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                            <div className="max-w-md mx-auto">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <SparklesIcon className="h-8 w-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-800 mb-2">No features yet</h3>
                                <p className="text-slate-500 mb-6">
                                    Get started by adding your first feature
                                </p>
                                <button
                                    onClick={handleAddFeature}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                                >
                                    Create First Feature
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Features Grid */
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {features.map((feature) => (
                                <FeatureCard
                                    key={feature.id}
                                    feature={feature}
                                    onEdit={handleEditFeature}
                                    onDelete={handleDeleteFeature}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
                <div className="space-y-6">
                    {/* Add Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleAddStat}
                            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Add New Stat
                        </button>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                                    <div className="w-16 h-16 bg-slate-200 rounded-2xl mb-4"></div>
                                    <div className="h-8 bg-slate-200 rounded mb-2"></div>
                                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                                </div>
                            ))}
                        </div>
                    ) : stats.length === 0 ? (
                        /* Empty State */
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                            <div className="max-w-md mx-auto">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <ChartBarIcon className="h-8 w-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-800 mb-2">No statistics yet</h3>
                                <p className="text-slate-500 mb-6">
                                    Get started by adding your first statistic
                                </p>
                                <button
                                    onClick={handleAddStat}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                                >
                                    Create First Stat
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Stats Grid */
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {stats.map((stat) => (
                                <StatCard
                                    key={stat.id}
                                    stat={stat}
                                    onEdit={handleEditStat}
                                    onDelete={handleDeleteStat}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Feature Modal */}
            {isFeatureModalOpen && (
                <FeatureModal
                    feature={editingFeature}
                    onClose={handleFeatureModalClose}
                    onSave={handleFeatureSave}
                />
            )}

            {/* Stat Modal */}
            {isStatModalOpen && (
                <StatModal
                    stat={editingStat}
                    onClose={handleStatModalClose}
                    onSave={handleStatSave}
                />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && settings && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-6">Section Settings</h2>
                    <div className="space-y-6 max-w-2xl">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => handleSettingsChange('section_title', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Why Choose Aham Housing Finance?"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle
                            </label>
                            <textarea
                                value={settings.section_subtitle}
                                onChange={(e) => handleSettingsChange('section_subtitle', e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                                placeholder="Your trusted partner in home financing"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Call-to-Action Button Text
                            </label>
                            <input
                                type="text"
                                value={settings.cta_text}
                                onChange={(e) => handleSettingsChange('cta_text', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Get Started"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Button Link
                            </label>
                            <input
                                type="text"
                                value={settings.cta_link}
                                onChange={(e) => handleSettingsChange('cta_link', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="/contact"
                            />
                        </div>
                        <div className="pt-4">
                            <button
                                onClick={handleSettingsSave}
                                disabled={savingSettings}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm Dialogs */}
            <ConfirmDialog
                isOpen={deleteFeatureConfirm.isOpen}
                onClose={() => setDeleteFeatureConfirm({ isOpen: false, featureId: null })}
                onConfirm={confirmDeleteFeature}
                title="Delete Feature"
                message="Are you sure you want to delete this feature? This action cannot be undone."
            />

            <ConfirmDialog
                isOpen={deleteStatConfirm.isOpen}
                onClose={() => setDeleteStatConfirm({ isOpen: false, statId: null })}
                onConfirm={confirmDeleteStat}
                title="Delete Statistic"
                message="Are you sure you want to delete this statistic? This action cannot be undone."
            />
        </div>
    );
};

export default WhyChoosePage;
